import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { caseService, estudianteService, userService } from "@/services/api";

const CATEGORIAS = [
  "convivencia", "orientacion", "psicologia", "vulneracion",
  "judicializado", "conducta", "asistencia", "apoderado", "otro"
];

const PRIORIDADES = ["baja", "media", "alta", "critica"];

export default function CasoNuevoPage() {
  const navigate = useNavigate();
  const [students, setStudents] = useState<any[]>([]);
  const [responsables, setResponsables] = useState<any[]>([]);
  const [form, setForm] = useState({
    studentId: "",
    titulo: "",
    descripcion: "",
    categoria: "convivencia",
    tipo: "",
    prioridad: "media",
    responsableId: "",
    fechaApertura: new Date().toISOString().split("T")[0],
    fechaLimite: "",
    judicializado: false,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadData() {
      try {
        const [studentsData, usersData] = await Promise.all([
          estudianteService.list(),
          userService.list()
        ]);
        if (Array.isArray(studentsData)) {
          setStudents(studentsData);
        }
        if (Array.isArray(usersData)) {
          setResponsables(usersData.filter((u: any) =>
            ["orientador", "convivencia", "psicologo", "inspector", "director"].includes(u.rol)
          ));
        }
      } catch (err: any) {
        console.error("Failed to load students/users from API:", err);
        setError("Error al cargar datos desde Azure: " + (err.response?.data?.detail || err.message));
      }
    }
    loadData();
  }, []);

  const selectedStudent = students.find((s) => s.id === form.studentId || s.id === String(form.studentId));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      await caseService.create({
        estudiante_id: form.studentId,
        tipo: form.categoria === "psicologia" ? "psicologo" : form.categoria,
        subtipo: form.tipo || null,
        titulo: form.titulo,
        descripcion: form.descripcion,
        prioridad: form.prioridad,
        fecha_limite: form.fechaLimite || null,
        responsable_id: form.responsableId || null,
        judicializado: !!form.judicializado,
        confidencial: form.categoria === "psicologia"
      });
      navigate("/casos");
    } catch (err: any) {
      console.error("Failed to create case in API:", err);
      setError("Error al crear caso en base de datos Azure: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/casos")}
          className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer mb-3"
        >
          <span className="flex items-center justify-center w-4 h-4"><i className="ri-arrow-left-line text-sm" /></span>
          Volver a Casos
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-950">Abrir Nuevo Caso</h1>
        <p className="text-sm text-foreground-500 mt-0.5">Registra un nuevo caso de convivencia escolar, orientación o psicología</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Student selection */}
        <div className="bg-white border border-background-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider">Datos del Estudiante</h2>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Estudiante</label>
            <select
              value={form.studentId}
              onChange={(e) => updateField("studentId", e.target.value)}
              required
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent cursor-pointer"
            >
              <option value="">Seleccionar estudiante</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre} - {s.curso}</option>
              ))}
            </select>
          </div>

          {selectedStudent && (
            <div className="grid grid-cols-2 gap-3 text-sm bg-background-50 rounded-lg p-3">
              <div>
                <span className="text-foreground-500">Curso:</span>{" "}
                <span className="font-medium text-foreground-900">{selectedStudent.curso}</span>
              </div>
              <div>
                <span className="text-foreground-500">Profesor Jefe:</span>{" "}
                <span className="font-medium text-foreground-900">{selectedStudent.profesorJefe}</span>
              </div>
              <div>
                <span className="text-foreground-500">RUT:</span>{" "}
                <span className="font-medium text-foreground-900">{selectedStudent.rut}</span>
              </div>
            </div>
          )}
        </div>

        {/* Case details */}
        <div className="bg-white border border-background-200 rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider">Detalles del Caso</h2>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Título del caso <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.titulo}
              onChange={(e) => updateField("titulo", e.target.value)}
              required
              placeholder="Ej: Agresión verbal a profesor en aula"
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Descripción inicial</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => updateField("descripcion", e.target.value)}
              rows={4}
              placeholder="Describe los hechos de forma objetiva: qué ocurrió, quiénes participaron, dónde y cuándo..."
              className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent resize-none"
              maxLength={500}
            />
            <p className="text-xs text-foreground-400 mt-1">{form.descripcion.length}/500 caracteres</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Categoría</label>
              <select
                value={form.categoria}
                onChange={(e) => updateField("categoria", e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent cursor-pointer capitalize"
              >
                {CATEGORIAS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo</label>
              <input
                type="text"
                value={form.tipo}
                onChange={(e) => updateField("tipo", e.target.value)}
                placeholder="Ej: Agresión verbal a funcionario"
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Prioridad</label>
              <div className="flex gap-2">
                {PRIORIDADES.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => updateField("prioridad", p)}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors cursor-pointer capitalize whitespace-nowrap ${
                      form.prioridad === p
                        ? p === "critica" ? "bg-red-50 border-red-300 text-red-700"
                          : p === "alta" ? "bg-orange-50 border-orange-300 text-orange-700"
                          : p === "media" ? "bg-yellow-50 border-yellow-300 text-yellow-700"
                          : "bg-background-100 border-background-300 text-foreground-600"
                        : "border-background-200 text-foreground-500 hover:bg-background-50"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Responsable</label>
              <select
                value={form.responsableId}
                onChange={(e) => updateField("responsableId", e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent cursor-pointer"
              >
                {responsables.map((r) => (
                  <option key={r.id} value={r.id}>{r.nombre} ({r.rol})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Fecha de Apertura</label>
              <input
                type="date"
                value={form.fechaApertura}
                onChange={(e) => updateField("fechaApertura", e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Fecha Límite</label>
              <input
                type="date"
                value={form.fechaLimite}
                onChange={(e) => updateField("fechaLimite", e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={form.judicializado}
                onChange={(e) => updateField("judicializado", e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-background-200 rounded-full peer-checked:bg-purple-500 after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
            </label>
            <div>
              <span className="text-sm font-medium text-foreground-700">Caso judicializado</span>
              <p className="text-xs text-foreground-400">Marcar si existe requerimiento de tribunal o causa judicial asociada</p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving || !form.titulo || !form.studentId}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {saving ? (
              <>
                <span className="flex items-center justify-center w-4 h-4 animate-spin"><i className="ri-loader-4-line" /></span>
                Guardando...
              </>
            ) : (
              <>
                <span className="flex items-center justify-center w-4 h-4"><i className="ri-save-line" /></span>
                Crear Caso
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate("/casos")}
            className="px-6 py-2.5 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}